import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgentContext } from '../../context/AgentContext';
import { createAgent } from '../../services/agentService';
import { 
  Scale, 
  Calculator, 
  FileText, 
  HelpCircle, 
  Building2,
  FileSpreadsheet,
  ArrowRight,
  Sparkles,
  Loader2,
  Send,
  Paperclip,
  X
} from 'lucide-react';

export default function QuickActions() {
  const { agents, refreshAgents } = useContext(AgentContext);
  const navigate = useNavigate();
  const [loadingTopic, setLoadingTopic] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const chatInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  // Accepted file types
  const ACCEPTED_TYPES = [
    'application/pdf',
    'image/png', 'image/jpeg', 'image/webp',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ];
  const ACCEPTED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.docx', '.doc', '.xlsx', '.xls'];

  const isFileAccepted = (file) => {
    if (ACCEPTED_TYPES.includes(file.type)) return true;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    return ACCEPTED_EXTENSIONS.includes(ext);
  };

  const addPendingFiles = (newFiles) => {
    if (!newFiles || newFiles.length === 0) return;
    const accepted = Array.from(newFiles).filter(isFileAccepted);
    if (accepted.length === 0) return;
    setPendingFiles((prev) => {
      const existingKeys = new Set(prev.map(f => `${f.name}-${f.size}`));
      const toAdd = accepted.filter(f => !existingKeys.has(`${f.name}-${f.size}`));
      return [...prev, ...toAdd].slice(0, 10);
    });
  };

  const removePendingFile = (indexToRemove) => {
    setPendingFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addPendingFiles(e.dataTransfer.files);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pasted = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file && isFileAccepted(file)) {
          pasted.push(file);
        }
      }
    }
    if (pasted.length > 0) {
      addPendingFiles(pasted);
    }
  };

  const handleChatSubmit = async () => {
    const text = chatInput.trim();
    if ((!text && pendingFiles.length === 0) || chatLoading) return;
    setChatLoading(true);
    try {
      const filesToSend = [...pendingFiles];
      sessionStorage.setItem("tacia_pending_prompt", text);
      sessionStorage.setItem("tacia_auto_send", "true");

      const title = text
        ? (text.length > 35 ? text.substring(0, 35) + "..." : text)
        : (filesToSend.length > 0 ? `Analisis: ${filesToSend[0].name}` : "Konsultasi Pajak");

      const newAgent = {
        agentName: title,
        agentPersonality: "Professional",
        agentPrompt: "Kamu asisten AI perpajakan...",
        businessDescription: "",
      };
      const res = await createAgent(newAgent);
      const newAgentId = res?.id;
      await refreshAgents();

      const targetAgentId = newAgentId || (agents && agents.length > 0 ? agents[0].id : null);
      if (targetAgentId) {
        navigate(`/agents/${targetAgentId}`, {
          state: {
            initialPrompt: text,
            initialFiles: filesToSend,
            autoSend: true
          }
        });
      }
    } catch (err) {
      console.error("Gagal membuat sesi konsultasi:", err);
      if (agents && agents.length > 0) {
        navigate(`/agents/${agents[0].id}`, {
          state: {
            initialPrompt: text,
            initialFiles: pendingFiles,
            autoSend: true
          }
        });
      }
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSubmit();
    }
  };

  // Auto-resize textarea as user types, with smooth scrolling when exceeding max-height
  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.style.height = "auto";
      const scrollH = chatInputRef.current.scrollHeight;
      const nextHeight = Math.min(Math.max(scrollH, 56), 180);
      chatInputRef.current.style.height = `${nextHeight}px`;
      chatInputRef.current.style.overflowY = scrollH > 180 ? "auto" : "hidden";
    }
  }, [chatInput]);

  const consultTopics = [
    {
      icon: FileSpreadsheet,
      title: 'Bukti Potong Coretax (Excel → XML)',
      desc: 'Konversi data Excel ke XML Bupot Unifikasi & PPh 21/26 (BPA1, BPA2, BP21, BP26, BPMP).',
      color: '#3273D9',
      templatePrompt: 'Halo Tacia, saya ingin memproses dan membuat file XML Bukti Pemotongan (Bupot) untuk Coretax DJP dari file Excel template resmi. Tolong bantu verifikasi data dan generate XML-nya.',
    },
    {
      icon: Building2,
      title: 'SPT Tahunan Badan (XML Coretax)',
      desc: 'Generate XML Lampiran 3B, 9, 10A, 11A (Promosi, Kredit, Piutang, dll), dan Pencatatan.',
      color: '#10B981',
      templatePrompt: 'Halo Tacia, saya ingin membuat file XML Lampiran SPT Tahunan PPh Badan Coretax (Lampiran 3B / 9 / 10A / 11A). Tolong pandu prosesnya.',
    },
    {
      icon: FileText,
      title: 'Draft Dokumen Perpajakan (Word)',
      desc: 'Pilih langsung jenis draf surat resmi (.docx): Keberatan, SPHP, SP2DK, Pengurangan Sanksi, atau Surat Kuasa.',
      color: '#6366F1',
      templatePrompt: 'Halo Tacia, bantu saya menyusun draf resmi Surat Keberatan atas Surat Ketetapan Pajak (SKPKB/SKPLB) dalam format Word.',
      subPrompts: [
        { label: 'Surat Keberatan', prompt: 'Halo Tacia, bantu saya menyusun draf resmi Surat Keberatan atas Surat Ketetapan Pajak (SKPKB/SKPLB) dalam format Word.' },
        { label: 'Tanggapan SPHP', prompt: 'Halo Tacia, bantu saya menyusun draf resmi Surat Tanggapan atas SPHP (Surat Pemberitahuan Hasil Pemeriksaan) dalam format Word.' },
        { label: 'Tanggapan SP2DK', prompt: 'Halo Tacia, bantu saya menyusun draf resmi Surat Tanggapan / Klarifikasi atas SP2DK dalam format Word.' },
        { label: 'Pengurangan Sanksi', prompt: 'Halo Tacia, bantu saya menyusun draf resmi Surat Permohonan Pengurangan atau Penghapusan Sanksi Administrasi (Pasal 36 ayat 1a UU KUP) dalam format Word.' },
        { label: 'Pembatalan STP', prompt: 'Halo Tacia, bantu saya menyusun draf resmi Surat Permohonan Pengurangan atau Pembatalan STP yang Tidak Benar (Pasal 36 ayat 1c UU KUP) dalam format Word.' },
        { label: 'Surat Kuasa Khusus', prompt: 'Halo Tacia, bantu saya menyusun draf resmi Surat Kuasa Khusus perpajakan sesuai PMK 44 Tahun 2026 dalam format Word.' },
      ],
    },
    {
      icon: Scale,
      title: 'Konsultasi Regulasi & Riset Pajak',
      desc: 'Tanya jawab UU HPP, PMK, PER DJP, tarif pajak, dan analisis keabsahan status regulasi.',
      color: '#F59E0B',
      templatePrompt: 'Halo Tacia, saya ingin berkonsultasi mengenai ketentuan regulasi perpajakan yang berlaku terkait kewajiban pajak perusahaan.',
    },
    {
      icon: Calculator,
      title: 'Simulasi & Perhitungan PPh 21',
      desc: 'Kalkulasi tarif efektif TER bulanan, tarif progresif Pasal 17, dan skema Gross-Up tunjangan.',
      color: '#EC4899',
      templatePrompt: 'Halo Tacia, tolong bantu saya membuat simulasi perhitungan PPh 21 menggunakan skema tarif efektif (TER) dan metode Gross-Up tunjangan pajak.',
    },
    {
      icon: HelpCircle,
      title: 'Panduan & Solusi CoreTax DJP',
      desc: 'Bimbingan fitur Coretax, deposit pajak, validasi NPWP 16 digit, kode objek, dan impor XML.',
      color: '#3B82F6',
      templatePrompt: 'Halo Tacia, saya butuh panduan mengenai sistem Coretax DJP, pembuatan deposit pajak, dan pemecahan kendala impor XML bukti potong.',
    },
  ];

  const handleTopicClick = async (topic) => {
    if (loadingTopic) return;
    setLoadingTopic(topic.title);
    try {
      sessionStorage.setItem("tacia_pending_prompt", topic.templatePrompt);

      const newAgent = {
        agentName: topic.title,
        agentPersonality: "Professional",
        agentPrompt: "Kamu asisten AI perpajakan...",
        businessDescription: "",
      };
      const res = await createAgent(newAgent);
      const newAgentId = res?.id;
      await refreshAgents();

      if (newAgentId) {
        navigate(`/agents/${newAgentId}`, {
          state: { initialPrompt: topic.templatePrompt }
        });
      } else if (agents && agents.length > 0) {
        navigate(`/agents/${agents[0].id}`, {
          state: { initialPrompt: topic.templatePrompt }
        });
      }
    } catch (err) {
      console.error("Gagal membuat sesi konsultasi:", err);
      if (agents && agents.length > 0) {
        navigate(`/agents/${agents[0].id}`, {
          state: { initialPrompt: topic.templatePrompt }
        });
      } else {
        alert("Gagal memulai sesi baru. Silakan coba lagi.");
      }
    } finally {
      setLoadingTopic(null);
    }
  };

  return (
    <div>
      {/* ── GREETING ── */}
      <div className="text-center pt-8 sm:pt-16 pb-4 sm:pb-6">
        <h2 className="text-2xl sm:text-4xl font-sans text-text-pri font-bold tracking-tight">
          Apa yang ingin Anda konsultasikan?
        </h2>
        <p className="text-sm sm:text-base text-text-sec mt-2">
          Mulai konsultasi baru atau pilih topik di bawah
        </p>
      </div>

      {/* ── CENTERED CHAT INPUT (ChatGPT / Claude Style) ── */}
      <div className="max-w-2xl mx-auto px-4 mb-10 sm:mb-14">
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`
            group relative flex flex-col rounded-2xl
            bg-card-bg border-2
            shadow-[0_4px_24px_-4px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]
            hover:shadow-[0_12px_36px_-6px_rgba(50,115,217,0.12)]
            hover:border-brand/40
            focus-within:border-brand
            focus-within:ring-4 focus-within:ring-brand/15
            focus-within:shadow-[0_16px_40px_-6px_rgba(50,115,217,0.22)]
            transition-all duration-300
            ${isDragging ? 'border-brand ring-4 ring-brand/20 bg-brand-low/10' : 'border-ledger-border/90'}
          `}
        >
          {/* Drop overlay */}
          {isDragging && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-card-bg/95 backdrop-blur-sm rounded-2xl border-2 border-dashed border-brand pointer-events-none animate-fadeIn">
              <Paperclip className="w-8 h-8 text-brand animate-bounce mb-2" />
              <p className="text-sm font-semibold text-text-pri">Lepaskan file atau gambar di sini</p>
              <p className="text-xs text-text-sec mt-0.5">Mendukung PDF, Word, Excel, JPG, PNG</p>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.doc,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                addPendingFiles(e.target.files);
              }
              e.target.value = "";
            }}
          />

          {/* Subtle top accent gradient shimmer */}
          <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-2xl bg-gradient-to-r from-transparent via-brand/40 to-transparent opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-300" />

          {/* Textarea area */}
          <div className="px-4 pt-3.5 pb-2">
            <textarea
              ref={chatInputRef}
              rows={2}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleChatKeyDown}
              onPaste={handlePaste}
              placeholder="Tanyakan seputar perpajakan, draf surat, atau drop file dokumen/gambar di sini..."
              disabled={chatLoading}
              className="
                w-full bg-transparent
                focus:outline-none
                resize-none
                text-[15px] sm:text-[16px] leading-relaxed text-text-pri
                placeholder:text-text-sec/50
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              style={{ minHeight: '56px', maxHeight: '180px' }}
            />
          </div>

          {/* Attached Files Preview Strip */}
          {pendingFiles.length > 0 && (
            <div className="px-4 pb-2.5 pt-1 border-t border-ledger-border/40">
              <div className="flex items-center justify-between mb-1.5 text-[11px] text-text-sec font-medium">
                <span className="flex items-center gap-1 text-brand font-semibold">
                  <Paperclip size={11} />
                  {pendingFiles.length} file terlampir
                </span>
                <button
                  type="button"
                  onClick={() => setPendingFiles([])}
                  className="text-text-sec hover:text-red-500 transition-colors"
                >
                  Hapus Semua
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                {pendingFiles.map((f, idx) => (
                  <div
                    key={`${f.name}-${idx}`}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-surface-inset border border-ledger-border rounded-lg text-xs text-text-pri font-medium shadow-2xs"
                  >
                    <Paperclip size={12} className="text-brand shrink-0" />
                    <span className="truncate max-w-[180px]">{f.name}</span>
                    <span className="text-text-sec text-[10px]">({(f.size / 1024).toFixed(0)} KB)</span>
                    <button
                      type="button"
                      onClick={() => removePendingFile(idx)}
                      className="p-0.5 hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-colors"
                      title="Hapus file"
                    >
                      <X size={12} className="text-text-sec hover:text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bottom toolbar */}
          <div className="flex items-center justify-between px-4 pb-3 pt-1 border-t border-ledger-border/30">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={chatLoading}
                title="Lampirkan dokumen atau gambar (PDF, DOCX, XLSX, PNG, JPG)"
                className="
                  inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg
                  text-xs font-medium text-text-sec
                  hover:bg-brand-low hover:text-brand
                  border border-ledger-border/60 hover:border-brand/40
                  transition-all
                "
              >
                <Paperclip size={13} className="text-brand" />
                <span className="text-[11px] hidden sm:inline">Upload / Drop File</span>
              </button>

              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium bg-brand-low text-brand">
                <Sparkles size={12} className="shrink-0" />
                Konsultan Pajak AI
              </span>
            </div>

            <button
              onClick={handleChatSubmit}
              disabled={(!chatInput.trim() && pendingFiles.length === 0) || chatLoading}
              title="Kirim pertanyaan atau dokumen"
              className="
                flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-xl
                bg-brand text-white font-medium text-xs
                hover:bg-brand/90 active:scale-95
                disabled:bg-ledger-border disabled:text-text-sec disabled:cursor-not-allowed
                shadow-sm hover:shadow-md shadow-brand/20
                transition-all duration-200 shrink-0
              "
            >
              {chatLoading ? (
                <>
                  <Loader2 size={13} className="animate-spin" />
                  <span>Memproses...</span>
                </>
              ) : (
                <>
                  <span>Kirim</span>
                  <Send size={13} className="shrink-0" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── TOPIC CARDS ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
        {consultTopics.map((topic, idx) => {
          const IconComp = topic.icon;
          const isLoadingThis = loadingTopic === topic.title;

          return (
            <div
              key={idx}
              onClick={() => handleTopicClick(topic)}
              className="gold-card cursor-pointer p-5 flex flex-col justify-between h-[185px] group"
            >
              <div className="flex items-center justify-between shrink-0 mb-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 shrink-0"
                  style={{ backgroundColor: `${topic.color}18` }}
                >
                  <IconComp size={20} style={{ color: topic.color }} />
                </div>
                {isLoadingThis ? (
                  <Loader2 size={16} className="animate-spin text-brand" />
                ) : (
                  <ArrowRight 
                    size={16} 
                    className="text-text-sec group-hover:text-brand group-hover:translate-x-1 transition-all shrink-0" 
                  />
                )}
              </div>
              <div className="flex flex-col justify-end">
                <h3 className="font-semibold text-sm text-text-pri group-hover:text-brand transition-colors mb-1 line-clamp-2 flex items-start">
                  {topic.title}
                </h3>
                {topic.subPrompts ? (
                  <div className="flex flex-wrap gap-1 mt-1" onClick={(e) => e.stopPropagation()}>
                    {topic.subPrompts.map((sub, sIdx) => (
                      <button
                        key={sIdx}
                        type="button"
                        onClick={() => handleTopicClick({ ...topic, title: `Draft ${sub.label}`, templatePrompt: sub.prompt })}
                        className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-brand-low/80 text-brand hover:bg-brand hover:text-white border border-brand/20 transition-colors cursor-pointer"
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-text-sec leading-relaxed line-clamp-2 h-[36px]">
                    {topic.desc}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
